import React, { useState, useMemo } from 'react';
import { DIGITAL_ADS, HOMEPAGE_BANNER_AVAILABLE } from '../config';
import {
  getRunSchedule, getUpcomingWednesdays, toDateInputValue,
  calcPricing, getPrintAd, getDigitalAd, fmtDollars, getOnlyIssue,
} from '../utils/pricing';
import { getDigitalAvailability, isBookedThrough, findNextOpenStart } from '../utils/availability';

export default function Step2Placement({ form, setForm, availability, onNext, onBack }) {
  const [error, setError] = useState('');

  const isDigitalOnly = form.medium === 'digital';

  // If the advertiser already has dates chosen (e.g. navigated back), reflect
  // live availability here too. Before dates are set, nothing is marked sold out.
  const runSchedule = getRunSchedule(form.startDate, form.weeks);
  const digiAvail   = getDigitalAvailability(availability, runSchedule, form.digitalSkips || {});
  const wednesdays  = useMemo(() => getUpcomingWednesdays(52), []);
  const startYmd    = form.startDate ? toDateInputValue(form.startDate) : null;
  const runKeys     = runSchedule.map(toDateInputValue);
  // A one-issue print special can't move, so never offer a new start date.
  const runLocked   = !isDigitalOnly && !!getOnlyIssue(form.printAd);

  // Running weekly total. Weeks are chosen on the next step, so this shows the
  // nominal per-week rate; the full total (with any discount) appears there.
  const liveMedium  = isDigitalOnly ? 'digital' : form.digitalAds.length > 0 ? 'both' : 'print';
  const weeklyRate  = calcPricing({ ...form, medium: liveMedium }).weeklyRate;
  const printAd     = !isDigitalOnly && form.printAd ? getPrintAd(form.printAd) : null;
  const totalItems  = [
    printAd && printAd.label,
    ...form.digitalAds.map(id => (getDigitalAd(id) || {}).label),
  ].filter(Boolean);
  const totalLabel  = totalItems.length === 0
    ? 'Pick a placement'
    : totalItems.length === 1 && printAd
    ? `${printAd.label} (print only)`
    : totalItems.join(' + ');

  // "Next opening" on a sold-out placement: move the whole run to the first
  // start date where it's open every week, and select it, in one tap.
  function startOn(id, wed) {
    setForm(f => ({
      ...f,
      startDate:    wed,
      digitalAds:   f.digitalAds.includes(id) ? f.digitalAds : [...f.digitalAds, id],
      digitalSkips: { ...(f.digitalSkips || {}), [id]: [] },
    }));
    setError('');
  }

  // Or keep the dates (and any print ad on them) and run this placement only
  // in the weeks it's open.
  function skipFullWeeks(id, fullDates) {
    setForm(f => {
      const prev = (f.digitalSkips && f.digitalSkips[id]) || [];
      return {
        ...f,
        digitalAds:   f.digitalAds.includes(id) ? f.digitalAds : [...f.digitalAds, id],
        digitalSkips: { ...(f.digitalSkips || {}), [id]: [...new Set([...prev, ...fullDates])] },
      };
    });
    setError('');
  }

  function undoSkips(id) {
    setForm(f => ({
      ...f,
      digitalAds:   f.digitalAds.filter(x => x !== id),
      digitalSkips: { ...(f.digitalSkips || {}), [id]: [] },
    }));
  }

  function toggleDigital(id, soldOut) {
    if (soldOut) return;
    setForm(f => {
      const ads = f.digitalAds.includes(id)
        ? f.digitalAds.filter(x => x !== id)
        : [...f.digitalAds, id];
      return { ...f, digitalAds: ads };
    });
    setError('');
  }

  function handleSkip() {
    setForm(f => ({ ...f, digitalAds: [], medium: 'print' }));
    onNext();
  }

  function handleNext() {
    if (isDigitalOnly && form.digitalAds.length === 0) {
      setError('Please select at least one digital placement to continue.');
      return;
    }
    const newMedium = isDigitalOnly
      ? 'digital'
      : form.digitalAds.length > 0 ? 'both' : 'print';
    setForm(f => ({ ...f, medium: newMedium }));
    onNext();
  }

  return (
    <div className="step-card">
      <div className="digital-preview-wrap">
        <img
          src="/lightning_website_img.jpg"
          alt="Hendersonville Lightning website"
          className="digital-preview-img"
        />
        <div className="digital-preview-overlay">
          <span className="digital-preview-label">hendersonvillelightning.com</span>
        </div>
      </div>

      <h2 className="step-heading" style={{ marginTop: 20 }}>
        {isDigitalOnly ? 'Choose your digital placements' : 'Want to add digital reach?'}
      </h2>
      <p className="step-subheading">
        {isDigitalOnly
          ? 'Select one or more placements on our website and email newsletter.'
          : <>
              <strong>Optional</strong> &mdash; add online reach, or continue with print only.
              Add-on rates apply automatically with your print ad.
            </>}
      </p>

      {DIGITAL_ADS.map(ad => {
        const manualSoldOut = ad.requiresAvailability && !HOMEPAGE_BANNER_AVAILABLE;
        const liveSoldOut   = !!(digiAvail[ad.id] && digiAvail[ad.id].soldOut);
        const soldOut       = manualSoldOut || liveSoldOut;
        // Sold out only because of the manual bookedThrough date in config.js.
        const blackoutOnly  = liveSoldOut && !manualSoldOut &&
          digiAvail[ad.id].fullDates.every(d => isBookedThrough(ad, d));
        const checked      = form.digitalAds.includes(ad.id);
        const isNonprofit  = form.orgType === 'nonprofit';
        const standalone   = isNonprofit ? ad.nonprofitStandalonePrice : ad.standalonePrice;
        const addon        = isNonprofit ? ad.nonprofitAddonPrice      : ad.addonPrice;
        const price        = isDigitalOnly ? standalone : addon;
        const savings      = standalone - addon;

        // Ways out of a live sold-out (not the manual leaderboard switch).
        const fullDates  = liveSoldOut && !manualSoldOut ? digiAvail[ad.id].fullDates : [];
        const nextStart  = fullDates.length && !runLocked
          ? findNextOpenStart(ad, availability, wednesdays, form.weeks, startYmd)
          : null;
        const canSkip    = fullDates.length > 0 && fullDates.length < runKeys.length;
        // The weeks it would actually run if they keep their dates.
        const prevSkips  = (form.digitalSkips && form.digitalSkips[ad.id]) || [];
        const openDates  = runKeys.filter(d => !fullDates.includes(d) && !prevSkips.includes(d));
        const skipped    = checked && !soldOut
          ? ((form.digitalSkips && form.digitalSkips[ad.id]) || []).filter(d => runKeys.includes(d))
          : [];

        const showSkip   = canSkip && openDates.length > 0;
        const hasWayOut  = !!nextStart || showSkip;
        const nextYmd    = nextStart ? toDateInputValue(nextStart) : null;
        const nextRun    = nextStart ? getRunSchedule(nextStart, form.weeks).map(toDateInputValue) : [];

        return (
          <div key={ad.id} className={hasWayOut ? 'sold-out-group' : undefined}>
          <div
            className={`check-option${checked ? ' selected' : ''}${soldOut ? ' disabled sold-out' : ''}`}
            onClick={() => !soldOut && toggleDigital(ad.id, soldOut)}
            role="checkbox"
            aria-checked={checked}
            aria-disabled={soldOut}
            tabIndex={soldOut ? -1 : 0}
            onKeyDown={e =>
              !soldOut && (e.key === 'Enter' || e.key === ' ') && toggleDigital(ad.id, soldOut)
            }
          >
            <input
              type="checkbox"
              readOnly
              checked={checked}
              disabled={soldOut}
              tabIndex={-1}
            />
            <div className="option-info">
              <div className="option-label">
                {ad.label}
                {ad.id === 'leaderboard' && (
                  <span className="ad-label-note"> (Run of Site)</span>
                )}
                {soldOut && <span className="sold-out-badge">Sold out</span>}
              </div>
              <div className="option-dims">
                {ad.dimensions}
                {ad.id === 'square' && (
                  <span> &mdash; rotates upon page refresh with max 5 other advertisers</span>
                )}
              </div>
              {soldOut && (
                <div className="sold-out-tag">
                  {blackoutOnly
                    ? `Booked through ${fmtShortDate(ad.bookedThrough)}. Open again from ${fmtShortDate(ad.bookedThrough, 1)}.`
                    : liveSoldOut && !manualSoldOut
                    ? `Booked ${joinDates(fullDates.map(d => fmtShortDate(d)))}`
                    : 'Currently sold out — check back soon'}
                </div>
              )}
              {skipped.length > 0 && (
                <div className="price-skip-note">
                  Skips {skipped.map(d => fmtShortDate(d)).join(', ')} (full) &middot;{' '}
                  <button
                    type="button"
                    className="availability-remove-btn"
                    onClick={e => { e.stopPropagation(); undoSkips(ad.id); }}
                  >
                    undo
                  </button>
                </div>
              )}
              {!isDigitalOnly && !soldOut && savings > 0 && (
                <div className="option-addon-note">
                  Add-on rate &mdash; save ${savings} per week vs. standalone
                </div>
              )}
            </div>
            <div className="option-price-wrap">
              {!isDigitalOnly && !soldOut && savings > 0 && (
                <span className="option-price-original">${standalone}</span>
              )}
              <span className="option-price" style={soldOut ? { color: 'var(--disabled)' } : {}}>
                ${price}
              </span>
            </div>
          </div>
          {hasWayOut && (
            <div className="next-opening">
              <div className="next-opening-title">
                {nextStart ? `Next opening: ${fmtShortDate(nextYmd)}` : 'Some of your weeks are open'}
              </div>
              <div className="next-opening-choices">
                {nextStart && (
                  <button type="button" className="next-opening-choice" onClick={() => startOn(ad.id, nextStart)}>
                    <strong>
                      {form.weeks === 1
                        ? `Run ${fmtShortDate(nextYmd)} instead`
                        : `${form.weeks} weeks from ${fmtShortDate(nextYmd)}`}
                    </strong>
                    <span>
                      {nextRun.length > 1 && `${fmtShortDate(nextRun[0])} – ${fmtShortDate(nextRun[nextRun.length - 1])} · `}
                      ${price}/wk
                    </span>
                  </button>
                )}
                {showSkip && (
                  <button type="button" className="next-opening-choice" onClick={() => skipFullWeeks(ad.id, fullDates)}>
                    <strong>Just {joinDates(openDates.map(d => fmtShortDate(d)))}</strong>
                    <span>
                      Keeps your dates · {openDates.length} week{openDates.length !== 1 ? 's' : ''} · ${price}/wk
                    </span>
                  </button>
                )}
              </div>
              {nextStart && !isDigitalOnly && (
                <div className="next-opening-warn">
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M8 1.5 15 14H1z" fill="currentColor" />
                    <rect x="7.25" y="6" width="1.5" height="4.2" fill="#fff" />
                    <rect x="7.25" y="11" width="1.5" height="1.5" fill="#fff" />
                  </svg>
                  <span>Starting {fmtShortDate(nextYmd)} moves your print ad to {fmtShortDate(nextYmd)} too.</span>
                </div>
              )}
            </div>
          )}
          </div>
        );
      })}

      {error && <p className="step-error">{error}</p>}

      {/* Running total with Continue beside it, so the price and the button
          that commits to it read together. Sticks to the bottom on phones. */}
      <div className="total-bar">
        <div className="total-bar-info" aria-live="polite">
          <div className="total-bar-items">{totalLabel}</div>
          {/* A one-issue special runs once, so its total isn't a weekly rate. */}
          <div className="total-bar-amount">{fmtDollars(weeklyRate)}{runLocked ? ' total' : '/week'}</div>
        </div>
        <button className="btn btn-primary" onClick={handleNext}>
          Continue &rarr;
        </button>
      </div>

      <div className="nav-buttons nav-buttons-stack">
        <button className="btn btn-secondary nav-back" onClick={onBack}>&larr; Back</button>
        {!isDigitalOnly && (
          <button className="btn btn-secondary" onClick={handleSkip}>
            Skip &mdash; print only
          </button>
        )}
      </div>
    </div>
  );
}

// ['Nov. 4', 'Nov. 11', 'Nov. 18'] -> 'Nov. 4, Nov. 11 & Nov. 18'
function joinDates(list) {
  return list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

// '2026-11-03' -> 'Nov. 3' (plus `addDays`, so the day after reads 'Nov. 4').
function fmtShortDate(ymd, addDays = 0) {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d + addDays);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${month}${month === 'May' ? '' : '.'} ${date.getDate()}`;
}
